import { useEffect, useMemo, useState } from 'react';
import {
  Breadcrumbs,
  Box,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  LinearProgress,
  Alert,
  Link
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DownloadIcon from '@mui/icons-material/Download';
import { useDispatch, useSelector } from 'react-redux';
import {
  exportTransactionReports,
  fetchTransactionPartners,
  fetchTransactionReports
} from '../store/slices/transactionReportsSlice';

const timeframeOptions = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'this_year', label: 'This Year' },
  { value: 'custom', label: 'Custom' }
];

const columns = [
  { id: 'date', label: 'Date', sortable: true },
  { id: 'hour', label: 'Hour', sortable: true },
  { id: 'partnerName', label: 'Partner', sortable: true },
  { id: 'total', label: 'Total', sortable: true, align: 'right' },
  { id: 'success', label: 'Success', sortable: true, align: 'right' },
  { id: 'fail', label: 'Fail', sortable: true, align: 'right' },
  { id: 'usage', label: 'Usage', sortable: true, align: 'right' }
];

const getDefaultDate = () => new Date().toISOString().split('T')[0];

const TransactionReportPage = () => {
  const dispatch = useDispatch();
  const {
    items,
    meta,
    loading,
    error,
    query,
    partners,
    partnersLoading,
    partnersError,
    exportStatus,
    exportError
  } = useSelector((state) => state.transactionReports);

  const [timeframe, setTimeframe] = useState(query.timeframe);
  const [partnerId, setPartnerId] = useState(query.partnerId ?? 'ALL');
  const [customStart, setCustomStart] = useState(query.startDate ?? getDefaultDate());
  const [customEnd, setCustomEnd] = useState(query.endDate ?? getDefaultDate());
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [sortBy, setSortBy] = useState(query.sortBy);
  const [sortDirection, setSortDirection] = useState(query.sortDirection);
  const [page, setPage] = useState(Math.max((query.page ?? 1) - 1, 0));
  const [rowsPerPage, setRowsPerPage] = useState(query.pageSize);
  const [dense, setDense] = useState(false);

  const partnerOptions = useMemo(() => {
    const base = [{ value: 'ALL', label: 'All Partners' }];
    if (!partners || !partners.length) {
      return base;
    }
    return [
      ...base,
      ...partners.map((partner) => ({
        value: partner.id,
        label: partner.name
      }))
    ];
  }, [partners]);

  useEffect(() => {
    dispatch(fetchTransactionPartners());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      fetchTransactionReports({
        page: page + 1,
        pageSize: rowsPerPage,
        timeframe,
        startDate: timeframe === 'custom' ? customStart : null,
        endDate: timeframe === 'custom' ? customEnd : null,
        partnerId,
        sortBy,
        sortDirection
      })
    );
  }, [dispatch, timeframe, customStart, customEnd, partnerId, page, rowsPerPage, sortBy, sortDirection]);

  useEffect(() => {
    if (!meta) return;
    if (typeof meta.page === 'number' && meta.page - 1 !== page) {
      setPage(Math.max(meta.page - 1, 0));
    }
    if (typeof meta.page_size === 'number' && meta.page_size !== rowsPerPage) {
      setRowsPerPage(meta.page_size);
    }
  }, [meta, page, rowsPerPage]);

  const sortedItems = useMemo(() => {
    const data = [...items];
    data.sort((a, b) => {
      const aValue = a[sortBy] ?? '';
      const bValue = b[sortBy] ?? '';

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
    return data;
  }, [items, sortBy, sortDirection]);

  const paginatedItems = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedItems.slice(start, start + rowsPerPage);
  }, [sortedItems, page, rowsPerPage]);

  const handleSort = (columnId) => {
    if (sortBy === columnId) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(columnId);
      setSortDirection('asc');
    }
    setPage(0);
  };

  const formatDisplayDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('en-GB');
  };

  const handleExport = () => {
    setMenuAnchor(null);
    dispatch(
      exportTransactionReports({
        timeframe,
        startDate: timeframe === 'custom' ? customStart : null,
        endDate: timeframe === 'custom' ? customEnd : null,
        partnerId,
        sortBy,
        sortDirection
      })
    );
  };

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator="/"
            sx={{ fontSize: 12, mb: 1, color: 'text.secondary' }}
          >
            <Link underline="hover" color="inherit" href="#">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="#">
              Management
            </Link>
            <Typography color="text.primary">Transaction Report Detail</Typography>
          </Breadcrumbs>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Transaction Report Detail
          </Typography>
          <Typography color="text.secondary">
            Monitor transaction performance across channels and timeframes.
          </Typography>
        </Box>
        <IconButton
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          size="small"
          sx={{ border: '1px solid rgba(148, 163, 184, 0.32)' }}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
                  elevation: 6,
                  sx: {
                    boxShadow: '0px 22px 45px rgba(15, 23, 42, 0.18)',
                    border: '1px solid rgba(148, 163, 184, 0.16)',
                    background:
                      'radial-gradient(circle at top right, rgba(14, 165, 233, 0.24), transparent 55%), #ffffff',
                    borderRadius: 1,
                    mt: 1
                  }
                }}
        >
          <MenuItem onClick={handleExport} disabled={exportStatus === 'loading'}>
            <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
            {exportStatus === 'loading' ? 'Preparing...' : 'Download Excel'}
          </MenuItem>
        </Menu>
      </Stack>

      <Paper sx={{ p: { xs: 2.5, md: 3 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ md: 'flex-end' }}
        >
          <Stack spacing={2} sx={{ width: { xs: '100%', md: '60%' } }}>
            <FormControl sx={{ width: { xs: '100%', md: 240 } }}>
              <InputLabel>Timeframe</InputLabel>
              <Select
                value={timeframe}
                label="Timeframe"
                onChange={(event) => {
                  setTimeframe(event.target.value);
                  setPage(0);
                }}
              >
                {timeframeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {timeframe === 'custom' && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  type="date"
                  label="Start Date"
                  value={customStart}
                  onChange={(event) => setCustomStart(event.target.value)}
                  sx={{ width: { xs: '100%', sm: 200 } }}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  type="date"
                  label="End Date"
                  value={customEnd}
                  onChange={(event) => setCustomEnd(event.target.value)}
                  sx={{ width: { xs: '100%', sm: 200 } }}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            )}
          </Stack>

          <FormControl sx={{ width: { xs: '100%', md: 280 } }}>
            <InputLabel>Partner</InputLabel>
            <Select
              value={partnerId}
              label="Partner"
              onChange={(event) => {
                setPartnerId(event.target.value);
                setPage(0);
              }}
              MenuProps={{
                PaperProps: { sx: { maxHeight: 320 } }
              }}
            >
              {partnerOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {(loading || partnersLoading) && <LinearProgress sx={{ mt: 2 }} />}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {partnersError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {partnersError}
          </Alert>
        )}
        {exportError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {exportError}
          </Alert>
        )}

        <Box
          sx={{
            width: '100%',
            overflowX: 'auto',
            border: '1px solid rgba(148, 163, 184, 0.16)',
            borderRadius: 1,
            mt: 3,
            backgroundColor: 'background.paper',
            '&::-webkit-scrollbar': {
              height: 6
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(148, 163, 184, 0.35)',
              borderRadius: 999
            }
          }}
        >
          <TableContainer
            sx={{
              display: 'inline-block',
              minWidth: '100%',
              width: '100%'
            }}
          >
            <Table
              size={dense ? 'small' : 'medium'}
              sx={{
                minWidth: 900,
                borderCollapse: 'separate',
                borderSpacing: 0,
                '& thead th': {
                  backgroundColor: 'rgba(15, 23, 42, 0.02)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.08,
                  fontSize: 12,
                  borderBottom: '1px solid rgba(148, 163, 184, 0.24)'
                },
                '& tbody td': {
                  borderBottom: '1px solid rgba(148, 163, 184, 0.16)'
                },
                '& thead th:first-of-type': {
                  borderTopLeftRadius: 8
                },
                '& thead th:last-of-type': {
                  borderTopRightRadius: 8
                }
              }}
            >
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      sortDirection={sortBy === column.id ? sortDirection : false}
                    >
                      {column.sortable ? (
                        <TableSortLabel
                          active={sortBy === column.id}
                          direction={sortBy === column.id ? sortDirection : 'asc'}
                          onClick={() => handleSort(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedItems.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(15, 23, 42, 0.02)'
                      }
                    }}
                  >
                    <TableCell>{formatDisplayDate(row.date)}</TableCell>
                    <TableCell>{row.hour}</TableCell>
                    <TableCell>
                      <Typography fontWeight={600}>{row.partnerName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID #{row.partnerId}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{row.total.toLocaleString()}</TableCell>
                    <TableCell align="right">{row.success.toLocaleString()}</TableCell>
                    <TableCell align="right">{row.fail.toLocaleString()}</TableCell>
                    <TableCell align="right">{row.usage.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
                {!paginatedItems.length && !loading && (
                  <TableRow>
                    <TableCell colSpan={columns.length}>
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          No transactions found
                        </Typography>
                        <Typography color="text.secondary">
                          Adjust the filters or timeframe to view additional records.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={1.5}
          sx={{ mt: 2 }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={dense}
                onChange={(event) => setDense(event.target.checked)}
              />
            }
            label="Dense"
          />
          <TablePagination
            component="div"
            count={meta?.total_items ?? sortedItems.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              '& .MuiTablePagination-toolbar': {
                px: { xs: 0, sm: 2 }
              }
            }}
          />
        </Stack>
      </Paper>
    </Stack>
  );
};

export default TransactionReportPage;
